import { Platform } from '../types';

export type IConfig<T> = T & {
  isDefault: boolean;
};

export interface IConfigResult<T> {
  isDefault: boolean;
  result: T;
}

export interface IPlatformFulfillmentConfig {
  isDeliverySupported: boolean;
  isEmailConfirmationRequired: boolean;
  isPaymentRequired: boolean;
}

export const DEFAULT_PLATFORM_CONFIGURATION: IPlatformFulfillmentConfig = {
  isDeliverySupported: true,
  isEmailConfirmationRequired: true,
  isPaymentRequired: true,
};

export const PLATFORM_CONFIGURATION: Partial<Record<Platform, IPlatformFulfillmentConfig>> = {
  [Platform.waitr]: {
    isDeliverySupported: false,
    isEmailConfirmationRequired: false,
    isPaymentRequired: false,
  },
  [Platform.grubhub]: {
    isDeliverySupported: false,
    isEmailConfirmationRequired: false,
    isPaymentRequired: false,
  },
  [Platform.biteninja]: {
    isDeliverySupported: false,
    isEmailConfirmationRequired: false,
    isPaymentRequired: false,
  },
  [Platform.doordash]: {
    isDeliverySupported: false,
    isEmailConfirmationRequired: false,
    isPaymentRequired: false,
  },
  [Platform.skip]: {
    isDeliverySupported: false,
    isEmailConfirmationRequired: false,
    isPaymentRequired: false,
  },
  [Platform.uber]: {
    isDeliverySupported: false,
    isEmailConfirmationRequired: false,
    isPaymentRequired: false,
  },
};

/**
 * Takes an order cart and returns fulfillment configuration for the correct platform
 * @param {Platform}
 * @returns a configuration object
 */
export function getConfigurationByPlatform(
  platform?: Platform,
): IConfig<IPlatformFulfillmentConfig> {
  const config = platform && PLATFORM_CONFIGURATION[platform];
  if (!config) {
    return { ...DEFAULT_PLATFORM_CONFIGURATION, isDefault: true };
  }
  return { ...config, isDefault: false };
}

/**
 * Returns a tuple of result, default stating if the payment is required
 * for the given platform
 */
export function isPaymentRequired(platform?: Platform): IConfigResult<boolean> {
  const { isPaymentRequired, isDefault } = getConfigurationByPlatform(platform);
  return { isDefault, result: isPaymentRequired };
}

/**
 * Returns a tuple of result, default stating if delivery is required
 * for the given platform
 */
export function isDeliverySupported(platform?: Platform): IConfigResult<boolean> {
  const { isDeliverySupported, isDefault } = getConfigurationByPlatform(platform);
  return { isDefault, result: isDeliverySupported };
}

/**
 * Returns a tuple of result, default stating if the email confirmation
 * is required for the given platform
 */
export function isEmailConfirmationRequired(platform?: Platform): IConfigResult<boolean> {
  const { isEmailConfirmationRequired, isDefault } = getConfigurationByPlatform(platform);
  return { isDefault, result: isEmailConfirmationRequired };
}
